import React from 'react';
import { defineMessages, IntlShape, useIntl } from 'react-intl';
import { tiktokCommercialLabel, TikTokPostSettings } from 'fym-shared/src/music24/tiktokPostSettings';
import type { YouTubePostSettings } from 'fym-shared/src/music24/youtubePostSettings';
import { TypedMemo } from '../../../common/types';
import { youtubeMessages } from '../composer/youtube/youtubeMessages';
import { tiktokMessages } from '../composer/tiktok/tiktokMessages';
import type { CalendarPost } from '../types';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

const messages = defineMessages({
  commentsOn: { id: 'app.sml.promote.calendar.settings.commentsOn', defaultMessage: 'Comments on' },
  duetOn: { id: 'app.sml.promote.calendar.settings.duetOn', defaultMessage: 'Duet on' },
  stitchOn: { id: 'app.sml.promote.calendar.settings.stitchOn', defaultMessage: 'Stitch on' },
  noInteractions: {
    id: 'app.sml.promote.calendar.settings.noInteractions',
    defaultMessage: 'Comments, Duet and Stitch off',
  },
});

const LEVEL_MESSAGES = {
  PUBLIC_TO_EVERYONE: tiktokMessages.privacyPublic,
  MUTUAL_FOLLOW_FRIENDS: tiktokMessages.privacyFriends,
  FOLLOWER_OF_CREATOR: tiktokMessages.privacyFollowers,
  SELF_ONLY: tiktokMessages.privacyOnlyMe,
} as const;

/** "Followers · Comments on · Paid partnership": what the user chose for the TikTok post. */
export function tiktokSettingsSummary(intl: IntlShape, settings: TikTokPostSettings): string {
  const parts: string[] = [intl.formatMessage(LEVEL_MESSAGES[settings.privacyLevel])];
  const on = [
    settings.allowComment ? intl.formatMessage(messages.commentsOn) : null,
    settings.allowDuet ? intl.formatMessage(messages.duetOn) : null,
    settings.allowStitch ? intl.formatMessage(messages.stitchOn) : null,
  ].filter((part): part is string => part !== null);
  parts.push(...(on.length > 0 ? on : [intl.formatMessage(messages.noInteractions)]));
  const label = tiktokCommercialLabel(settings);
  if (label !== null) {
    parts.push(
      intl.formatMessage(label === 'paidPartnership' ? tiktokMessages.labelPaid : tiktokMessages.labelPromotional)
    );
  }
  return parts.join(' · ');
}

const YOUTUBE_LEVELS = {
  public: youtubeMessages.privacyPublic,
  unlisted: youtubeMessages.privacyUnlisted,
  private: youtubeMessages.privacyPrivate,
} as const;

const kidsMessages = defineMessages({
  madeForKids: { id: 'app.sml.promote.calendar.settings.madeForKids', defaultMessage: 'Made for kids' },
  notForKids: { id: 'app.sml.promote.calendar.settings.notForKids', defaultMessage: 'Not made for kids' },
});

/** "Unlisted · Not made for kids": what the user chose for the YouTube video. */
export function youtubeSettingsSummary(intl: IntlShape, settings: YouTubePostSettings): string {
  return [
    intl.formatMessage(YOUTUBE_LEVELS[settings.privacyStatus]),
    intl.formatMessage(settings.madeForKids ? kidsMessages.madeForKids : kidsMessages.notForKids),
  ].join(' · ');
}

/** TikTok may take a few minutes to show a post (G8): the line shows for 15 minutes after it. */
const PROCESSING_WINDOW_MS = 15 * 60 * 1000;

export function isTikTokProcessing(post: Pick<CalendarPost, 'platform' | 'status' | 'postedAt'>, now: number): boolean {
  if (post.platform !== 'tiktok') {
    return false;
  }
  if (post.status === 'publishing') {
    return true;
  }
  return (
    post.status === 'posted' && post.postedAt !== null && now - new Date(post.postedAt).getTime() < PROCESSING_WINDOW_MS
  );
}

interface PostSettingsNoteProps {
  post: CalendarPost;
}

/**
 * What the post sheet says about the platform settings of a post: the choice in one line, and the
 * "may take a few minutes" line of TikTok right after the post went out.
 */
const PostSettingsNoteComponent: React.FC<PostSettingsNoteProps> = ({ post }) => {
  const intl = useIntl();
  const settings = post.platformSettings;
  let summary: string | null = null;
  if (post.platform === 'tiktok' && settings !== null && 'privacyLevel' in settings) {
    summary = tiktokSettingsSummary(intl, settings);
  } else if (post.platform === 'youtube' && settings !== null && 'privacyStatus' in settings) {
    summary = youtubeSettingsSummary(intl, settings);
  }
  const isProcessing = isTikTokProcessing(post, Date.now());
  if (summary === null && !isProcessing) {
    return null;
  }
  return (
    <div className={styles.dpBody} data-testid="post-settings-note">
      {summary === null ? null : <span className={styles.tiny}>{summary}</span>}
      {isProcessing ? <span className={styles.tiny}>{intl.formatMessage(tiktokMessages.processing)}</span> : null}
    </div>
  );
};

export const PostSettingsNote = TypedMemo(PostSettingsNoteComponent);
